export const WORLD_W = 80; // feet
export const WORLD_H = 60; // feet

export const LAYERS = {
  structural: { label: "Structural", color: "#F0F6FC", accent: "#8B949E" },
  electrical: { label: "Electrical", color: "#38BDF8", accent: "#38BDF8" },
  plumbing: { label: "Plumbing", color: "#34D399", accent: "#34D399" },
  hvac: { label: "HVAC", color: "#F59E0B", accent: "#F59E0B" },
  engineering: { label: "Structural Eng.", color: "#F43F5E", accent: "#F43F5E" },
  furniture: { label: "Furniture", color: "#E879F9", accent: "#E879F9" },
};

// kind -> {layer, label(short), title}
export const ITEM_KINDS = {
  door: { layer: "structural", label: "D", title: "Door" },
  window: { layer: "structural", label: "W", title: "Window" },
  outlet: { layer: "electrical", label: "⏦", title: "Outlet" },
  switch: { layer: "electrical", label: "S", title: "Switch" },
  panel: { layer: "electrical", label: "▣", title: "Breaker Panel" },
  light: { layer: "electrical", label: "✳", title: "Light Fixture" },
  sink: { layer: "plumbing", label: "◗", title: "Sink" },
  toilet: { layer: "plumbing", label: "T", title: "Toilet" },
  shower: { layer: "plumbing", label: "☂", title: "Shower" },
  water: { layer: "plumbing", label: "◆", title: "Water Supply" },
  drain: { layer: "plumbing", label: "○", title: "Drain" },
  register: { layer: "hvac", label: "▤", title: "Register" },
  duct: { layer: "hvac", label: "═", title: "Duct" },
  condenser: { layer: "hvac", label: "❄", title: "Condenser" },
  column: { layer: "engineering", label: "C", title: "Structural Column" },
  beam: { layer: "engineering", label: "B", title: "Load Beam" },
  footing: { layer: "engineering", label: "F", title: "Footing" },
  joist: { layer: "engineering", label: "J", title: "Floor Joist" },
  shearwall: { layer: "engineering", label: "L", title: "Load-Bearing Wall" },
  pier: { layer: "engineering", label: "P", title: "Foundation Pier / Stilt" },
  retaining: { layer: "engineering", label: "R", title: "Retaining Wall" },
  bed: { layer: "furniture", label: "Bed", title: "Bed", w: 6, h: 7, furniture: true },
  sofa: { layer: "furniture", label: "Sofa", title: "Sofa", w: 7, h: 3, furniture: true },
  table: { layer: "furniture", label: "Table", title: "Dining Table", w: 4, h: 4, furniture: true },
  chair: { layer: "furniture", label: "Chair", title: "Chair", w: 2, h: 2, furniture: true },
  desk: { layer: "furniture", label: "Desk", title: "Desk", w: 5, h: 2.5, furniture: true },
  stove: { layer: "furniture", label: "Stove", title: "Stove / Range", w: 2.5, h: 2.5, furniture: true },
  fridge: { layer: "furniture", label: "Fridge", title: "Refrigerator", w: 3, h: 3, furniture: true },
  bathtub: { layer: "furniture", label: "Tub", title: "Bathtub", w: 5, h: 2.5, furniture: true },
  wardrobe: { layer: "furniture", label: "Closet", title: "Wardrobe", w: 4, h: 2, furniture: true },
  stair: { layer: "furniture", label: "Stairs", title: "Staircase", w: 3.5, h: 9, furniture: true, stair: true },
};

let _id = 0;
export const uid = () => `el_${Date.now().toString(36)}_${_id++}`;

// MEP "run" line kinds — click-drag drawn duct/pipe runs (like walls but layered)
export const RUN_KINDS = {
  "duct-run": { layer: "hvac", label: "Supply Duct", color: "#F59E0B", dash: "1.2 0.8", width: 0.6 },
  "return-run": { layer: "hvac", label: "Return Duct", color: "#FBBF24", dash: "0.6 0.6", width: 0.6 },
  "pipe-run": { layer: "plumbing", label: "Water Supply", color: "#34D399", dash: "", width: 0.35 },
  "waste-run": { layer: "plumbing", label: "Waste / Drain", color: "#22D3EE", dash: "1.6 0.6", width: 0.5 },
};

export function normalizeRuns(runs) {
  return (runs || [])
    .filter((r) => r && RUN_KINDS[r.tool] && [r.x1, r.y1, r.x2, r.y2].every((v) => v !== undefined && v !== null))
    .map((r) => ({
      id: uid(), tool: r.tool,
      x1: Math.max(0, Number(r.x1) || 0), y1: Math.max(0, Number(r.y1) || 0),
      x2: Math.max(0, Number(r.x2) || 0), y2: Math.max(0, Number(r.y2) || 0),
    }));
}

export const SAMPLE_BLUEPRINT = {
  name: "Nordic Minimalist 3-Bed",
  rooms: [
    { id: uid(), name: "Living Room", x: 0, y: 0, w: 20, h: 18 },
    { id: uid(), name: "Kitchen", x: 20, y: 0, w: 16, h: 14 },
    { id: uid(), name: "Dining", x: 36, y: 0, w: 14, h: 14 },
    { id: uid(), name: "Bathroom", x: 50, y: 0, w: 10, h: 10 },
    { id: uid(), name: "Master Bedroom", x: 0, y: 18, w: 18, h: 16 },
    { id: uid(), name: "Bedroom 2", x: 18, y: 18, w: 15, h: 15 },
    { id: uid(), name: "Bedroom 3", x: 33, y: 18, w: 15, h: 15 },
    { id: uid(), name: "Garage", x: 50, y: 14, w: 22, h: 20 },
  ],
  items: [
    { id: uid(), kind: "door", layer: "structural", x: 10, y: 18 },
    { id: uid(), kind: "window", layer: "structural", x: 4, y: 0 },
    { id: uid(), kind: "window", layer: "structural", x: 14, y: 0 },
    { id: uid(), kind: "panel", layer: "electrical", x: 51, y: 15 },
    { id: uid(), kind: "outlet", layer: "electrical", x: 2, y: 2 },
    { id: uid(), kind: "outlet", layer: "electrical", x: 18, y: 16 },
    { id: uid(), kind: "light", layer: "electrical", x: 10, y: 9 },
    { id: uid(), kind: "switch", layer: "electrical", x: 19, y: 2 },
    { id: uid(), kind: "sink", layer: "plumbing", x: 22, y: 2 },
    { id: uid(), kind: "toilet", layer: "plumbing", x: 52, y: 2 },
    { id: uid(), kind: "shower", layer: "plumbing", x: 57, y: 2 },
    { id: uid(), kind: "register", layer: "hvac", x: 8, y: 12 },
    { id: uid(), kind: "register", layer: "hvac", x: 26, y: 8 },
    { id: uid(), kind: "condenser", layer: "hvac", x: 70, y: 2 },
    { id: uid(), kind: "column", layer: "engineering", x: 20, y: 18 },
    { id: uid(), kind: "column", layer: "engineering", x: 36, y: 18 },
    { id: uid(), kind: "column", layer: "engineering", x: 50, y: 14 },
    { id: uid(), kind: "beam", layer: "engineering", x: 33, y: 9 },
    { id: uid(), kind: "footing", layer: "engineering", x: 0, y: 34 },
    { id: uid(), kind: "shearwall", layer: "engineering", x: 50, y: 25 },
  ],
};

export const roomArea = (r) => Math.round(r.w * r.h);
export const totalArea = (rooms) => (rooms || []).reduce((s, r) => s + r.w * r.h, 0);
export const snap = (v, step = 0.5) => Math.round(v / step) * step;

// normalize AI response rooms/items into our schema with ids
export function normalizeBlueprint(data) {
  const rooms = (data.rooms || []).map((r) => ({
    id: uid(),
    name: r.name || "Room",
    x: Math.max(0, Number(r.x) || 0),
    y: Math.max(0, Number(r.y) || 0),
    w: Math.max(2, Number(r.w) || 10),
    h: Math.max(2, Number(r.h) || 10),
  }));
  const items = (data.items || [])
    .filter((it) => ITEM_KINDS[it.kind])
    .map((it) => ({
      id: uid(),
      kind: it.kind,
      layer: ITEM_KINDS[it.kind].layer,
      x: Math.max(0, Number(it.x) || 0),
      y: Math.max(0, Number(it.y) || 0),
    }));
  return { name: data.name || "AI Generated Plan", rooms, items, walls: normalizeWalls(data.walls) };
}

// normalize wall segments {x1,y1,x2,y2,thickness} in feet
export function normalizeWalls(walls) {
  return (walls || [])
    .filter((w) => w && [w.x1, w.y1, w.x2, w.y2].every((v) => v !== undefined && v !== null))
    .map((w) => ({
      id: uid(),
      x1: Math.max(0, Number(w.x1) || 0), y1: Math.max(0, Number(w.y1) || 0),
      x2: Math.max(0, Number(w.x2) || 0), y2: Math.max(0, Number(w.y2) || 0),
      thickness: Math.max(0.2, Math.min(1.5, Number(w.thickness) || 0.4)),
      ...(Array.isArray(w.openings) ? { openings: w.openings } : {}),
      ...(w.exterior ? { exterior: true } : {}),
    }));
}

// Normalize an AI multi-floor response into floors[] with fresh ids
export function normalizeFloors(data) {
  const raw = (data.floors || []).slice(0, 4);
  const floors = raw.map((f, i) => {
    const nb = normalizeBlueprint(f);
    return { name: f.name || `Floor ${i + 1}`, rooms: nb.rooms, items: nb.items, walls: nb.walls };
  });
  if (floors.length) return floors;
  const nb = normalizeBlueprint(data);
  return [{ name: "Ground Floor", rooms: nb.rooms, items: nb.items }];
}
